// Pure token derivation for the theming engine (no DOM, no imports) so the
// unit test can assert that `deriveTokens(DARK_THEME)` reproduces today's
// literal rgba values byte-for-byte for solo (unbranded) users.

// "#0f0" | "#00f5a0" | "rgb(...)" → [r,g,b]; null when unparseable
export function hexToRgb(hex) {
  if (typeof hex !== "string") return null;
  const s = hex.trim();
  const m3 = /^#([0-9a-f]{3})$/i.exec(s);
  if (m3) return m3[1].split("").map(c => parseInt(c + c, 16));
  const m6 = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(s);
  if (m6) { const h = m6[1]; return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16)); }
  const mr = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i.exec(s);
  if (mr) return [mr[1], mr[2], mr[3]].map(Number);
  return null;
}

// rgba() literal in the exact spelling theme.js uses (no spaces)
export const rgba = (hex, a) => {
  const c = hexToRgb(hex) || [0, 0, 0];
  return `rgba(${c[0]},${c[1]},${c[2]},${a})`;
};

// Alpha recipe per mode — these are the numbers baked into theme.js today.
const RECIPE = {
  dark:  { cardGlow: [0.09, 0.03], glow: 0.45, ambient: [0.085, 0.06, 0.055] },
  light: { cardGlow: [0.08, 0.03], glow: 0.35, ambient: [0.07, 0.06, 0.05] },
};

// Tokens that are functions of the accent colours: cardGlow, glow, ambient.
// (`shadow` / `shadowLg` are neutral black/navy and are left untouched.)
export function deriveTokens(t) {
  const r = RECIPE[t.mode === "light" ? "light" : "dark"];
  const accent = t.accent, accent2 = t.accent2, purple = t.purple;
  return {
    cardGlow: `linear-gradient(165deg, ${rgba(accent, r.cardGlow[0])} 0%, ${rgba(accent2, r.cardGlow[1])} 100%)`,
    glow: `0 4px 20px -4px ${rgba(accent, r.glow)}`,
    ambient:
      `radial-gradient(ellipse 70% 45% at 18% -8%, ${rgba(accent, r.ambient[0])}, transparent 60%),` +
      `radial-gradient(ellipse 60% 40% at 95% 4%, ${rgba(accent2, r.ambient[1])}, transparent 55%),` +
      `radial-gradient(ellipse 75% 55% at 50% 115%, ${rgba(purple, r.ambient[2])}, transparent 60%)`,
  };
}

// Relative luminance (WCAG) — used to pick a readable foreground on the accent
export function luminance(hex) {
  const c = hexToRgb(hex);
  if (!c) return 0;
  const f = (v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  return 0.2126 * f(c[0]) + 0.7152 * f(c[1]) + 0.0722 * f(c[2]);
}

// Compose the base palette for `mode` with the brand's colours.
// dark: bg=bg_dark, accent, accent2 · light: bg=bg_light, accent=accent_light, accent2=accent2_light
export function composeBrand(base, brand) {
  if (!brand) return { ...base };
  const light = base.mode === "light";
  const pick = light
    ? { bg: brand.bg_light, accent: brand.accent_light || brand.accent, accent2: brand.accent2_light || brand.accent2 }
    : { bg: brand.bg_dark, accent: brand.accent, accent2: brand.accent2 };
  const merged = { ...base };
  for (const k of Object.keys(pick)) if (hexToRgb(pick[k])) merged[k] = pick[k];
  return merged;
}

// Foreground for text on the accent: brand value, else black/white by luminance
export function onAccentFor(merged, brand) {
  const light = merged.mode === "light";
  const fromBrand = light ? (brand?.on_accent_light || brand?.on_accent) : brand?.on_accent;
  if (fromBrand && hexToRgb(fromBrand)) return fromBrand;
  if (!brand) return light ? "#fff" : merged.bg; // today's onAccent(): V.bg in dark, #fff in light
  return luminance(merged.accent) > 0.4 ? "#08080d" : "#fff";
}
